'use client'

import * as Headless from '@headlessui/react'
import type React from 'react'
import { forwardRef } from 'react'
import { cn } from '@/frontend/utils'

const dateTypes = ['date', 'datetime-local', 'month', 'time', 'week'] as const
type DateType = (typeof dateTypes)[number]

enum INPUT_VARIANTS {
  SUCCESS = 'success',
  DEFAULT = 'default',
  ERROR = 'error'
}

const colors = {
  [INPUT_VARIANTS.SUCCESS]: 'border border-borderSuccess',
  [INPUT_VARIANTS.DEFAULT]: 'border border-borderPrimary hover:border-borderHoverOverlay',
  [INPUT_VARIANTS.ERROR]: 'border border-borderError hover:border-contentRed'
}
export interface InputProps extends Omit<Headless.InputProps, 'as' | 'className'> {
  className?: string
  type?: 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | DateType
  variant?: `${INPUT_VARIANTS}`
  valid?: boolean
}

export const Input = forwardRef(function Input(
  { className, variant = INPUT_VARIANTS.DEFAULT, ...props }: InputProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  return (
    <span
      data-slot='control'
      className={cn([
        // Basic layout
        'relative block w-full',
        // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
        'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-xs',
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        'dark:before:hidden',
        // Focus ring
        'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500',
        // Disabled state
        'has-data-disabled:opacity-50 has-data-disabled:before:bg-zinc-950/5 has-data-disabled:before:shadow-none',
        // Invalid state
        'has-data-invalid:before:shadow-red-500/10',
        className
      ])}
    >
      <Headless.Input
        ref={ref}
        {...props}
        className={cn([
          // Date classes
          colors[variant],
          props.type &&
            dateTypes.includes(props.type as DateType) && [
              '[&::-webkit-datetime-edit-fields-wrapper]:p-0',
              '[&::-webkit-date-and-time-value]:min-h-[1.5em]',
              '[&::-webkit-datetime-edit]:inline-flex',
              '[&::-webkit-datetime-edit]:p-0',
              '[&::-webkit-datetime-edit-year-field]:p-0',
              '[&::-webkit-datetime-edit-month-field]:p-0',
              '[&::-webkit-datetime-edit-day-field]:p-0',
              '[&::-webkit-datetime-edit-hour-field]:p-0',
              '[&::-webkit-datetime-edit-minute-field]:p-0',
              '[&::-webkit-datetime-edit-second-field]:p-0',
              '[&::-webkit-datetime-edit-millisecond-field]:p-0',
              '[&::-webkit-datetime-edit-meridiem-field]:p-0'
            ],
          // Basic layout
          'relative block w-full appearance-none rounded-lg px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
          // Typography
          'text-base/6 text-contentPrimary placeholder:text-contentInverseTertiary',
          // Border
          // 'border-borderPrimary data-hover:border-borderHoverOverlay border',
          // Background color
          'bg-transparent',
          // Hide default focus styles
          'focus:outline-hidden',
          // Error state
          'data-invalid:data-hover:border-borderHoverOverlay data-invalid:border-borderError',
          // Success state
          'data-valid:data-hover:border-borderHoverOverlay data-valid:border-borderSuccess',
          // Disabled state
          'data-disabled:border-zinc-950/20 dark:data-hover:data-disabled:border-white/15 dark:data-disabled:border-white/15 dark:data-disabled:bg-white/2.5',
          // System icons
          'dark:scheme-dark'
        ])}
      />
    </span>
  )
})
